import { httpsCallable } from 'firebase/functions';
import {
  db,
  functions,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from '../config/firebase';

// ─────────────────────────────────────────────────────────────────────────────
// Schema notes (verified against firestore.rules + functions/index.js):
//   businesses.{ ownerId, verificationStatus: 'pending'|'verified'|'rejected', isVerified, isActive, businessName, email, ... }
//   veterinarians.{ userId, verificationStatus: 'pending'|'approved'|'rejected', isVerified, firstName, lastName, email, ... }
//   users.{ isAdmin, isBanned, banReason, displayName, firstName, lastName, email, lastSeen, createdAt, ... }
//   reports.{ userId (reporter), reportedId, reportedUserId, type, reason, status, ... }
// All admin write paths now go through Cloud Functions to preserve email + push notifications.
// ─────────────────────────────────────────────────────────────────────────────

// ==================== DASHBOARD ANALYTICS ====================

const callGetAdminDashboardStats = httpsCallable(functions, 'getAdminDashboardStats');

export const getDashboardStats = async () => {
  try {
    const res = await callGetAdminDashboardStats({});
    return res.data?.stats || {};
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

// ==================== HEALTH TRAJECTORY (AI TRAINING) ====================

const callGetTrainingReadiness = httpsCallable(functions, 'getTrainingReadiness');
const callGetAgreementMetric = httpsCallable(functions, 'getAgreementMetric');

// Data-collection progress toward the first trained model.
export const getTrainingReadiness = async () => {
  const res = await callGetTrainingReadiness({});
  return res.data;
};

// AI-vs-vet agreement metric (the moat-proof number) — admins get global stats.
export const getAgreementMetric = async () => {
  const res = await callGetAgreementMetric({});
  return res.data;
};

const dateKey = (d) => d.toISOString().split('T')[0];

const buildDailySeries = (timestamps, days) => {
  const buckets = new Map();
  for (let i = days - 1; i >= 0; i -= 1) {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - i);
    buckets.set(dateKey(date), 0);
  }
  for (const ts of timestamps) {
    if (!ts) continue;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (!d || isNaN(d.getTime())) continue;
    d.setHours(0, 0, 0, 0);
    const key = dateKey(d);
    if (buckets.has(key)) buckets.set(key, buckets.get(key) + 1);
  }
  return Array.from(buckets.entries()).map(([date, count]) => ({ date, count }));
};

export const getUserGrowthData = async (days = 30) => {
  const since = new Date();
  since.setDate(since.getDate() - days);
  since.setHours(0, 0, 0, 0);
  const snap = await getDocs(
    query(collection(db, 'users'), where('createdAt', '>=', Timestamp.fromDate(since)))
  );
  const timestamps = snap.docs.map((d) => d.data().createdAt);
  return buildDailySeries(timestamps, days);
};

export const getOrdersTrendData = async (days = 30) => {
  const since = new Date();
  since.setDate(since.getDate() - days);
  since.setHours(0, 0, 0, 0);
  const snap = await getDocs(
    query(collection(db, 'orders'), where('createdAt', '>=', Timestamp.fromDate(since)))
  );
  const timestamps = snap.docs.map((d) => d.data().createdAt);
  return buildDailySeries(timestamps, days);
};

// ==================== USER MANAGEMENT ====================

const callBanUser = httpsCallable(functions, 'adminBanUser');
const callUnbanUser = httpsCallable(functions, 'adminUnbanUser');
const callDeleteUserAccount = httpsCallable(functions, 'adminDeleteUserAccount');
const callExportUserData = httpsCallable(functions, 'adminExportUserData');
const callEraseUserData = httpsCallable(functions, 'adminEraseUserData');
const callForceLogoutUser = httpsCallable(functions, 'adminForceLogoutUser');

export const getUserById = async (userId) => {
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (!userDoc.exists()) return null;
  return { id: userDoc.id, ...userDoc.data() };
};

export const banUser = async (userId, reason) => {
  const res = await callBanUser({ uid: userId, reason });
  return res.data;
};

export const unbanUser = async (userId) => {
  const res = await callUnbanUser({ uid: userId });
  return res.data;
};

// Hard delete — for spam / fraud accounts. Use eraseUserData for GDPR.
export const deleteUserAccount = async ({ targetUserId, reason }) => {
  const res = await callDeleteUserAccount({ targetUserId, reason });
  return res.data;
};

// GDPR right-to-export. Returns the user's data bundle as JSON.
export const exportUserData = async ({ targetUserId }) => {
  const res = await callExportUserData({ targetUserId });
  return res.data;
};

// GDPR right-to-erasure. Anonymizes content, deletes profile + auth user.
export const eraseUserData = async ({ targetUserId, reason }) => {
  const res = await callEraseUserData({ targetUserId, reason });
  return res.data;
};

// Revokes refresh tokens; user is logged out within 1h on next refresh.
export const forceLogoutUser = async ({ targetUserId, reason }) => {
  const res = await callForceLogoutUser({ targetUserId, reason });
  return res.data;
};

// ==================== GDPR / DSA COMPLIANCE ====================

const callCreateSarRequest = httpsCallable(functions, 'adminCreateSarRequest');
const callUpdateSarRequest = httpsCallable(functions, 'adminUpdateSarRequest');
const callSetProcessingRestriction = httpsCallable(functions, 'adminSetProcessingRestriction');
const callLogModerationAction = httpsCallable(functions, 'adminLogModerationAction');

// SAR types per GDPR Articles 15-22.
export const SAR_TYPES = ['access', 'export', 'rectify', 'erase', 'restrict', 'object', 'portability'];
export const SAR_STATUSES = ['pending', 'in_progress', 'fulfilled', 'rejected', 'extended'];

export const createSarRequest = async ({ targetUserId, type, source, notes }) => {
  const res = await callCreateSarRequest({ targetUserId, type, source, notes });
  return res.data;
};

export const updateSarRequest = async ({ sarRequestId, status, notes, fulfillmentRef }) => {
  const res = await callUpdateSarRequest({ sarRequestId, status, notes, fulfillmentRef });
  return res.data;
};

// List SAR requests directly via Firestore — admin reads are allowed.
export const listSarRequests = async ({ status = 'all', maxResults = 200 } = {}) => {
  const ref = collection(db, 'sarRequests');
  const q = status === 'all'
    ? query(ref, orderBy('createdAt', 'desc'), limit(maxResults))
    : query(ref, where('status', '==', status), orderBy('createdAt', 'desc'), limit(maxResults));
  const snap = await getDocs(q);
  return {
    success: true,
    requests: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
  };
};

// Toggle GDPR Art. 18 "restriction of processing" on a user.
export const setProcessingRestriction = async ({ targetUserId, restricted, reason }) => {
  const res = await callSetProcessingRestriction({ targetUserId, restricted, reason });
  return res.data;
};

// DSA Article 17 reason codes for content moderation.
export const MODERATION_REASON_CODES = [
  { value: 'spam',             label: 'Spam' },
  { value: 'hate_speech',      label: 'Hate speech' },
  { value: 'harassment',       label: 'Harassment / bullying' },
  { value: 'violence',         label: 'Violence / threats' },
  { value: 'sexual_content',   label: 'Sexual content' },
  { value: 'illegal_content',  label: 'Illegal content' },
  { value: 'misinformation',   label: 'Misinformation' },
  { value: 'copyright_dmca',   label: 'Copyright / DMCA' },
  { value: 'self_harm',        label: 'Self-harm' },
  { value: 'child_safety',     label: 'Child safety' },
  { value: 'impersonation',    label: 'Impersonation' },
  { value: 'other',            label: 'Other' },
];

// Records the structured DSA reason for any moderation decision. Call this
// after the underlying mutation (softDeletePost, deletePost, moderateReport,
// etc.) to keep the moderationDecisions ledger in sync.
export const logModerationAction = async ({ targetType, targetId, action, reasonCode, notes, userId }) => {
  const res = await callLogModerationAction({ targetType, targetId, action, reasonCode, notes, userId });
  return res.data;
};

// ==================== BUSINESS MANAGEMENT ====================

const callApproveBusiness = httpsCallable(functions, 'adminApproveBusiness');
const callRejectBusiness = httpsCallable(functions, 'adminRejectBusiness');
const callGetAllBusinesses = httpsCallable(functions, 'getAllBusinesses');

export const getBusinessApplications = async ({ status = 'all', limit: limitCount = 100 } = {}) => {
  const res = await callGetAllBusinesses({ status, limit: limitCount });
  return { businesses: res.data?.businesses || [] };
};

export const getBusinessById = async (businessId) => {
  const businessDoc = await getDoc(doc(db, 'businesses', businessId));
  if (!businessDoc.exists()) return null;
  return { id: businessDoc.id, ...businessDoc.data() };
};

export const approveBusiness = async (businessId, _adminNotes = '') => {
  const res = await callApproveBusiness({ businessId });
  return res.data;
};

export const rejectBusiness = async (businessId, reason) => {
  const res = await callRejectBusiness({ businessId, reason });
  return res.data;
};

// ==================== VETERINARIAN MANAGEMENT ====================

const callApproveVet = httpsCallable(functions, 'adminApproveVet');
const callRejectVet = httpsCallable(functions, 'adminRejectVet');
const callGetAllVeterinarians = httpsCallable(functions, 'getAllVeterinarians');

export const getVetApplications = async ({ status = 'all' } = {}) => {
  const res = await callGetAllVeterinarians({ status });
  return { vets: res.data?.veterinarians || [] };
};

export const getVetById = async (vetId) => {
  const vetDoc = await getDoc(doc(db, 'veterinarians', vetId));
  if (!vetDoc.exists()) return null;
  return { id: vetDoc.id, ...vetDoc.data() };
};

// `businessId` is only set for vets that live inside
// businesses/{id}.vetServices.veterinarians[] rather than in the standalone
// `veterinarians` collection. The Cloud Function falls back to scanning
// businesses when it is omitted, so passing it is purely an optimisation.
export const approveVet = async (vetId, businessId = null) => {
  const res = await callApproveVet({ vetId, businessId: businessId || undefined });
  return res.data;
};

export const rejectVet = async (vetId, reason, businessId = null) => {
  if (!reason) throw new Error('Rejection reason is required');
  const res = await callRejectVet({ vetId, reason, businessId: businessId || undefined });
  return res.data;
};

// ==================== SUSPEND / UNSUSPEND (business or vet) ====================

const callSuspendEntity = httpsCallable(functions, 'adminSuspendEntity');
const callUnsuspendEntity = httpsCallable(functions, 'adminUnsuspendEntity');

// Single CF dispatches on entityType. Use 'business' or 'vet'. Sets
// status='suspended' + isActive=false + preserves previousVerificationStatus.
// Audit-logged. Push-notifies the owner best-effort.
export const suspendEntity = async ({ entityType, entityId, reason }) => {
  const res = await callSuspendEntity({ entityType, entityId, reason });
  return res.data;
};

export const unsuspendEntity = async ({ entityType, entityId }) => {
  const res = await callUnsuspendEntity({ entityType, entityId });
  return res.data;
};

const callReactivateEntity = httpsCallable(functions, 'adminReactivateEntity');

// Revert a rejected entity back to "pending" so it can be reapproved.
export const reactivateEntity = async ({ entityType, entityId, reason }) => {
  const res = await callReactivateEntity({ entityType, entityId, reason });
  return res.data;
};

// ==================== EDIT BUSINESS / VET PROFILE ====================

const callUpdateEntityProfile = httpsCallable(functions, 'adminUpdateEntityProfile');

// `updates` is filtered server-side against a whitelist of safe fields.
// Business: businessName, description, phone, email, website, address, city, country, businessType
// Vet:      firstName, lastName, clinicName, description, phone, email, address, city, country, specializations
export const updateEntityProfile = async ({ entityType, entityId, updates }) => {
  const res = await callUpdateEntityProfile({ entityType, entityId, updates });
  return res.data;
};

// ==================== REPORTS / CONTENT MODERATION ====================

const callModerateReport = httpsCallable(functions, 'adminModerateReport');

export const getReports = async ({ status = 'all', type = 'all', limit: limitCount = 100 } = {}) => {
  const constraints = [];
  if (status !== 'all') constraints.push(where('status', '==', status));
  if (type !== 'all') constraints.push(where('type', '==', type));
  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(limit(limitCount));
  const snap = await getDocs(query(collection(db, 'reports'), ...constraints));
  return {
    reports: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
  };
};

export const moderateReport = async (reportId, action, notes = '') => {
  const res = await callModerateReport({ reportId, action, notes });
  return res.data;
};

export const resolveReport = async (reportId, action, notes = '') =>
  moderateReport(reportId, action, notes);

export const dismissReport = async (reportId, notes = '') =>
  moderateReport(reportId, 'dismiss', notes);

// ==================== ORDERS ====================

const callUpdateOrderStatus = httpsCallable(functions, 'adminUpdateOrderStatus');

export const getOrders = async ({ status = 'all', limit: limitCount = 100 } = {}) => {
  const constraints = [];
  if (status !== 'all') constraints.push(where('status', '==', status));
  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(limit(limitCount));
  const snap = await getDocs(query(collection(db, 'orders'), ...constraints));
  return {
    orders: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
  };
};

export const updateOrderStatus = async (orderId, status, notes = '') => {
  const res = await callUpdateOrderStatus({ orderId, status, notes });
  return res.data;
};

// ==================== POSTS ====================

const callDeletePost = httpsCallable(functions, 'adminDeletePost');
const callSoftDeletePost = httpsCallable(functions, 'adminSoftDeletePost');

export const deletePost = async (postId, reason = '') => {
  const res = await callDeletePost({ postId, reason });
  return res.data;
};

// Reversible alternative to deletePost. Sets hiddenByAdmin: true; pass
// restore: true to bring the post back. Audit-logged.
export const softDeletePost = async ({ postId, restore = false, reason = '' }) => {
  const res = await callSoftDeletePost({ postId, restore, reason });
  return res.data;
};

export const restorePost = async (postId) =>
  softDeletePost({ postId, restore: true });

// ==================== VET BOOKINGS ====================

const callCancelVetBooking = httpsCallable(functions, 'adminCancelVetBooking');

// Cancels a booking via Cloud Function so the action is audit-logged and the
// booking owner is push-notified. Replaces the previous direct-Firestore write.
export const cancelVetBooking = async ({ bookingId, reason }) => {
  const res = await callCancelVetBooking({ bookingId, reason });
  return res.data;
};

// ==================== MISSING PETS ====================

const callResolveMissingPet = httpsCallable(functions, 'adminResolveMissingPet');

export const resolveMissingPet = async (missingPetId, { hard = false, notes = '' } = {}) => {
  const res = await callResolveMissingPet({ missingPetId, hard, notes });
  return res.data;
};

// ==================== SUBSCRIPTIONS ====================

const callListSubscriptions = httpsCallable(functions, 'adminListSubscriptions');
const callRefundSubscription = httpsCallable(functions, 'adminRefundSubscription');
const callSubscriptionAnalytics = httpsCallable(functions, 'getAdminSubscriptionAnalytics');

export const listSubscriptions = async ({ sinceDays = 365 } = {}) => {
  const res = await callListSubscriptions({ sinceDays });
  return res.data;
};

export const refundSubscription = async ({ transactionId, amount, reason }) => {
  const res = await callRefundSubscription({ transactionId, amount, reason });
  return res.data;
};

const callUpdateSubscription = httpsCallable(functions, 'adminUpdateSubscription');

// Force-cancel without refund, change tier, or change status. Refunds go
// through refundSubscription instead.
export const updateSubscription = async ({ userId, tier, billingCycle, status, reason }) => {
  const res = await callUpdateSubscription({ userId, tier, billingCycle, status, reason });
  return res.data;
};

export const cancelSubscription = async ({ userId, reason = 'Admin force-cancel (no refund)' }) =>
  updateSubscription({ userId, status: 'cancelled', reason });

export const changeSubscriptionTier = async ({ userId, tier, billingCycle, reason }) =>
  updateSubscription({ userId, tier, billingCycle, reason });

export const getSubscriptionAnalytics = async () => {
  const res = await callSubscriptionAnalytics({});
  return res.data;
};

// ==================== PAYMENT LEDGER ====================
// Backed by the `payments` collection. See
// docs/superpowers/specs/2026-08-20-payment-ledger-admin-revenue-design.md

const callListPayments = httpsCallable(functions, 'adminListPayments');
const callReconcileSubscriptions = httpsCallable(functions, 'adminReconcileSubscriptions');

// Re-checks every subscription whose period ended but which still reads
// active/cancelling against the store, and downgrades the ones the store
// confirms are over. Runs nightly on its own; this is the on-demand trigger.
// Defaults to a dry run — nothing is written unless dryRun is false.
export const reconcileSubscriptions = async ({ dryRun = true } = {}) => {
  const res = await callReconcileSubscriptions({ dryRun });
  return res.data;
};
const callBackfillPayments = httpsCallable(functions, 'adminBackfillPayments');

export const listPayments = async ({
  platform = 'all',
  kind = 'all',
  environment = 'all',
  sinceDays = 365,
  searchEmail = '',
  maxResults = 500,
} = {}) => {
  const res = await callListPayments({ platform, kind, environment, sinceDays, searchEmail, maxResults });
  return res.data;
};

// dryRun defaults to true on the server too — reconstructing historical rows
// should always be previewed before it writes.
export const backfillPayments = async ({ dryRun = true } = {}) => {
  const res = await callBackfillPayments({ dryRun });
  return res.data;
};

// ==================== 2FA (TOTP) ====================

const callEnrollTotp = httpsCallable(functions, 'enrollAdminTotp');
const callConfirmTotp = httpsCallable(functions, 'confirmAdminTotp');
const callVerifyTotp = httpsCallable(functions, 'verifyAdminTotp');
const callDisableTotp = httpsCallable(functions, 'disableAdminTotp');
const callTotpStatus = httpsCallable(functions, 'getAdminTotpStatus');
const callRegenerateTotpCodes = httpsCallable(functions, 'regenerateAdminTotpRecoveryCodes');

export const enrollTotp = async () => (await callEnrollTotp({})).data;
export const confirmTotp = async (code) => (await callConfirmTotp({ code })).data;
export const verifyTotp = async (code) => (await callVerifyTotp({ code })).data;
export const disableTotp = async () => (await callDisableTotp({})).data;
export const getTotpStatus = async () => (await callTotpStatus({})).data;
export const regenerateTotpCodes = async (code) => (await callRegenerateTotpCodes({ code })).data;

// ==================== ADMIN LOGIN NOTIFICATIONS ====================

const callNotifyLogin = httpsCallable(functions, 'notifyAdminLogin');

export const notifyAdminLogin = async ({ userAgent } = {}) => {
  try {
    const res = await callNotifyLogin({ userAgent });
    return res.data;
  } catch (err) {
    // Don't surface — login notifications are best-effort.
    console.warn('notifyAdminLogin failed:', err.message);
    return null;
  }
};

// ==================== ADMIN-CONFIG COLLECTIONS ====================
// Generic write/delete for `subscriptionPlans`, `discountCodes`, `appSettings`.
// Replaces direct setDoc/deleteDoc in those pages so each change is
// audit-logged. Server-side allowlist enforces the collection set.

const callWriteAdminConfig = httpsCallable(functions, 'adminWriteAdminConfig');
const callDeleteAdminConfig = httpsCallable(functions, 'adminDeleteAdminConfig');

export const writeAdminConfig = async ({ collection, docId, updates, merge = true }) => {
  const res = await callWriteAdminConfig({ collection, docId, updates, merge });
  return res.data;
};

export const deleteAdminConfig = async ({ collection, docId }) => {
  const res = await callDeleteAdminConfig({ collection, docId });
  return res.data;
};

// ==================== ADMIN ROLE MANAGEMENT ====================

const callSetAdminClaim = httpsCallable(functions, 'setAdminClaim');
const callListAdmins = httpsCallable(functions, 'listAdmins');

export const listAdmins = async () => {
  const res = await callListAdmins({});
  return {
    admins: res.data?.admins || [],
    hardcoded: res.data?.hardcoded || [],
  };
};

// Force the local Firebase Auth ID token to refresh so any newly-set custom
// claims (e.g. admin: true) are visible immediately without requiring sign-out.
const refreshIdTokenIfSelf = async (affectedUid) => {
  try {
    const { auth } = await import('../config/firebase');
    if (auth.currentUser && auth.currentUser.uid === affectedUid) {
      await auth.currentUser.getIdToken(true);
    }
  } catch (err) {
    console.warn('Failed to refresh ID token:', err.message);
  }
};

export const grantAdmin = async ({ email, uid }) => {
  const res = await callSetAdminClaim({ email, uid, value: true });
  if (res.data?.uid) await refreshIdTokenIfSelf(res.data.uid);
  return res.data;
};

export const revokeAdmin = async ({ email, uid }) => {
  const res = await callSetAdminClaim({ email, uid, value: false });
  if (res.data?.uid) await refreshIdTokenIfSelf(res.data.uid);
  return res.data;
};
